/**
 * WebSocket AI Avatar Authentication Manager
 * Handles storage, validation, and retrieval of WebSocket keys
 */

const WS_KEY_NAME = 'WSAIAvatarKey';
const WS_KEY_CREATED_TIME = 'WSAIAvatarKeyCreatedTime';
const KEY_EXPIRATION_MS = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
const WS_KEY_VOICE = 'WSAIAvatarVoiceId';

/**
 * Stores the WebSocket AI Avatar key in localStorage
 * @param key The WebSocket key to store
 */
export const storeWSAIAvatarKey = (key: string): void => {
  if (!key || key.trim() === '') return;

  localStorage.setItem(WS_KEY_NAME, key);
  localStorage.setItem(WS_KEY_CREATED_TIME, Date.now().toString());
};

/**
 * Validates if the stored WebSocket key exists and hasn't expired
 * @returns An object containing validation status and the key if valid
 */
export const validateWSAIAvatarKey = (): { isValid: boolean; key?: string } => {
  const storedKey = localStorage.getItem(WS_KEY_NAME);
  const storedTime = localStorage.getItem(WS_KEY_CREATED_TIME);

  if (!storedKey || !storedTime) {
    return { isValid: false };
  }

  const createdTime = parseInt(storedTime, 10);
  const currentTime = Date.now();
  const isExpired = currentTime - createdTime > KEY_EXPIRATION_MS;

  if (isExpired) {
    // Clear expired key
    localStorage.removeItem(WS_KEY_NAME);
    localStorage.removeItem(WS_KEY_CREATED_TIME);
    return { isValid: false };
  }

  return { isValid: true, key: storedKey };
};

/**
 * Gets a valid WebSocket key, prompting the user if needed
 * @param promptMessage Optional custom prompt message
 * @returns The WebSocket key or null if user cancels
 */
export const getValidWSAIAvatarKey = (promptMessage = 'Enter WebSocket AI Avatar key'): string | null => {
  const { isValid, key } = validateWSAIAvatarKey();

  if (isValid && key) {
    return key;
  }

  const newKey = prompt(promptMessage, '');

  if (newKey && newKey.trim() !== '') {
    storeWSAIAvatarKey(newKey);
    return newKey;
  }

  return null;
};


/**
 * Stores the AI Avatar VoiceId key in localStorage
 * @param key The WebSocket key to store
 */
export const storeWSAIAvatarVoiceId = (key: string): void => {
  if (!key || key.trim() === '') return;

  localStorage.setItem(WS_KEY_VOICE, key);
};
/**
 * Get the AI Avatar VoiceId key from localStorage
 * @param key The WebSocket key to store
 */
export const getWSAIAvatarVoiceId = (): string => {
  const storedKey = localStorage.getItem(WS_KEY_VOICE);

  if (!storedKey) return;

  return storedKey;
};
